/*
|--------------------------------------------------------------------------
| Shopify Free Gift Auto Add
|--------------------------------------------------------------------------
|
| Auto añade un producto regalo cuando existe un producto trigger
| en el carrito.
|
| Compatible con:
| - Shopify OS 2.0
| - Dawn
| - Cart Drawer
| - Cart Page
|
|--------------------------------------------------------------------------
*/

const TRIGGER_PRODUCT_ID = 9589964276059;
const GIFT_VARIANT_ID = 12452329324617;

let isProcessingGift = false;
let needsResync = false;
let syncTimer = null;
let isFetchHookInstalled = false;
let isXhrHookInstalled = false;
let isOwnCartRequest = false;

const CART_MUTATION_ENDPOINTS = [
  "/cart/add.js",
  "/cart/change.js",
  "/cart/update.js",
  "/cart/clear.js",
];

function scheduleSync(delay = 120) {
  if (syncTimer) {
    clearTimeout(syncTimer);
  }

  syncTimer = window.setTimeout(() => {
    syncTimer = null;
    syncFreeGift();
  }, delay);
}

function isCartMutationRequest(url) {
  return CART_MUTATION_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function installCartFetchHook() {
  if (isFetchHookInstalled || typeof window.fetch !== "function") return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      const rawUrl = args[0] instanceof Request ? args[0].url : String(args[0] || "");

      if (!isOwnCartRequest && response.ok && isCartMutationRequest(rawUrl)) {
        scheduleSync(120);
      }
    } catch (error) {
      console.error("[Free Gift Hook Error]", error);
    }

    return response;
  };

  isFetchHookInstalled = true;
}

function installCartXhrHook() {
  if (isXhrHookInstalled || typeof XMLHttpRequest === "undefined") return;

  const originalOpen = XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.open = function (...args) {
    const url = typeof args[1] === "string" ? args[1] : "";
    this.__freeGiftCartUrl = url;

    this.addEventListener("load", () => {
      if (
        !isOwnCartRequest &&
        this.status >= 200 &&
        this.status < 300 &&
        isCartMutationRequest(this.__freeGiftCartUrl || "")
      ) {
        scheduleSync(120);
      }
    });

    return originalOpen.apply(this, args);
  };

  isXhrHookInstalled = true;
}

async function getCart() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error("No se pudo obtener el carrito");
  }

  return response.json();
}

async function addGift(quantity) {
  isOwnCartRequest = true;

  try {
    return await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity,
            properties: { _auto_gift: "true" },
          },
        ],
      }),
    });
  } finally {
    isOwnCartRequest = false;
  }
}

async function setGiftQuantity(lineKey, quantity) {
  isOwnCartRequest = true;

  try {
    return await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
  } finally {
    isOwnCartRequest = false;
  }
}

function refreshCartUI() {
  // Delegamos completamente al mecanismo nativo del tema:
  // - CartDrawer._onCartRefresh hace fetch de ?section_id=cart-drawer y reemplaza el contenido
  // - CartCount._updateFromServer re-obtiene cart.js y actualiza el contador
  document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
}

async function syncFreeGift() {
  if (isProcessingGift) {
    needsResync = true;
    return;
  }

  isProcessingGift = true;
  needsResync = false;

  try {
    const cart = await getCart();

    const triggerQuantity = cart.items.reduce((total, item) => {
      if (item.product_id !== TRIGGER_PRODUCT_ID) return total;
      return total + item.quantity;
    }, 0);

    // Buscamos TODAS las líneas auto-regalo (el sistema de descuento de Shopify
    // puede partir una línea de qty=4 en dos de qty=2 cuando el descuento solo
    // cubre una parte de las unidades). Usamos filter+reduce para sumar el total.
    const autoGiftItems = cart.items.filter(
      (item) =>
        item.variant_id === GIFT_VARIANT_ID &&
        item.properties &&
        item.properties._auto_gift === "true"
    );
    const totalAutoGiftQty = autoGiftItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );
    let shouldRefresh = false;

    if (triggerQuantity > 0 && autoGiftItems.length === 0) {
      // No existe ninguna línea auto-regalo → añadir
      console.log(`[Free Gift] Añadiendo ${triggerQuantity} regalo(s)`);
      await addGift(triggerQuantity);
      shouldRefresh = true;
    } else if (
      triggerQuantity > 0 &&
      (totalAutoGiftQty !== triggerQuantity || autoGiftItems.length > 1)
    ) {
      // Total incorrecto O hay múltiples líneas auto-regalo → consolidar en una
      console.log(
        `[Free Gift] Consolidando regalo: ${totalAutoGiftQty} → ${triggerQuantity} (${autoGiftItems.length} línea(s))`
      );
      // Ajustamos la primera línea a la cantidad objetivo
      await setGiftQuantity(autoGiftItems[0].key, triggerQuantity);
      // Eliminamos las líneas duplicadas extra
      for (const extra of autoGiftItems.slice(1)) {
        await setGiftQuantity(extra.key, 0);
      }
      shouldRefresh = true;
    } else if (triggerQuantity === 0 && autoGiftItems.length > 0) {
      // El trigger fue eliminado → quitar todas las líneas auto-regalo
      console.log("[Free Gift] Eliminando regalo(s)");
      for (const gift of autoGiftItems) {
        await setGiftQuantity(gift.key, 0);
      }
      shouldRefresh = true;
    }

    if (shouldRefresh) {
      refreshCartUI();
    }
  } catch (error) {
    console.error("[Free Gift Error]", error);
  } finally {
    isProcessingGift = false;

    if (needsResync) {
      needsResync = false;
      scheduleSync(0);
    }
  }
}

function initFreeGift() {
  installCartFetchHook();
  installCartXhrHook();
  syncFreeGift();

  document.addEventListener("ajaxProduct:added", () => scheduleSync(50));
  document.addEventListener("product:added", () => scheduleSync(50));
  document.addEventListener("cart:updated", () => scheduleSync(50));
}

document.addEventListener("DOMContentLoaded", initFreeGift);
